import React, { useState } from 'react';
import {
  Box, Button, IconButton, Link, Snackbar, Tooltip, Typography,
} from '@mui/material';
import { VisibilityOffOutlined, VisibilityOutlined } from '@mui/icons-material';
import { useMutation } from '@apollo/client';
import { getUrlParameterByName } from '../../utilities';
import { SET_ANIMAL_MEDIA_HIDDEN } from '../../queries';

const TILE_SX = {
  p: 0, width: '80px', height: '80px', background: 'url(/photo_placeholder.png)', backgroundColor: '#E6E6E6', borderRadius: '0px',
};

function MediaThumbnail({
  setDialogImg, setDialogDate, setImgDialogOpen, animalName, animalMedia, index, isAdmin,
}: any) {
  const [setAnimalMediaHidden, { loading }] = useMutation(SET_ANIMAL_MEDIA_HIDDEN);
  const [errorOpen, setErrorOpen] = useState(false);

  const toggleHidden = () => {
    setAnimalMediaHidden({
      variables: { id: parseInt(animalMedia.id, 10), hidden: !animalMedia.hidden },
    }).catch(() => setErrorOpen(true));
  };

  const errorSnackbar = (
    <Snackbar
      open={errorOpen}
      autoHideDuration={4000}
      onClose={() => setErrorOpen(false)}
      message="Couldn't update photo visibility"
    />
  );

  // Admins get a Hide pill on visible tiles and a full "Hidden from community" overlay on hidden ones.
  // Non-admins (AWO staff) must see tiles exactly as before.
  const wrapTile = (tile: React.ReactElement) => {
    if (!isAdmin) return tile;
    return (
      <Box sx={{ position: 'relative', width: '80px', height: '80px' }}>
        {tile}
        {!animalMedia.hidden && (
          <Tooltip title="Hide from community">
            <IconButton
              aria-label="Hide from community"
              size="small"
              onClick={toggleHidden}
              disabled={loading}
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                zIndex: 1,
                color: '#fff',
                backgroundColor: 'rgba(23,24,26,0.72)',
                '&:hover': { backgroundColor: 'rgba(23,24,26,0.9)' },
              }}
            >
              <VisibilityOffOutlined sx={{ fontSize: 14 }} />
            </IconButton>
          </Tooltip>
        )}
        {animalMedia.hidden && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              zIndex: 1,
              backgroundColor: 'rgba(23,24,26,0.62)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '4px',
              textAlign: 'center',
              padding: '4px',
              // Let clicks fall through to the tile so hidden photos still enlarge; Unhide re-enables its own.
              pointerEvents: 'none',
            }}
          >
            <VisibilityOffOutlined sx={{ color: '#fff', fontSize: 16 }} />
            <Typography sx={{
              color: '#fff', fontSize: '10px', fontWeight: 700, lineHeight: 1.2, width: '100%', whiteSpace: 'normal',
            }}
            >
              Hidden from community
            </Typography>
            <Button
              onClick={toggleHidden}
              disabled={loading}
              sx={{
                backgroundColor: '#fff',
                color: 'text.primary',
                fontSize: '10px',
                fontWeight: 700,
                minWidth: 0,
                padding: '1px 8px',
                textTransform: 'none',
                pointerEvents: 'auto',
                '&:hover': { backgroundColor: '#eee' },
              }}
            >
              Unhide
            </Button>
          </Box>
        )}
        {errorSnackbar}
      </Box>
    );
  };

  if (animalMedia.fileType.startsWith('video') && animalMedia.url.includes('youtube')) {
    return wrapTile(
      <Button
        sx={TILE_SX}
        onClick={() => {
          setDialogImg(`https://www.youtube.com/embed/${getUrlParameterByName('v', animalMedia.url)}`);
          setDialogDate(animalMedia.dateCreated);
          setImgDialogOpen(true);
        }}
      >
        <img
          style={{ objectFit: 'cover', objectPosition: 'center' }}
          key={animalMedia.url}
          src={`https://img.youtube.com/vi/${getUrlParameterByName('v', animalMedia.url)}/default.jpg`}
          alt={`${animalName}-${index}`}
          loading="lazy"
          width="80px"
          height="80px"
        />
      </Button>,
    );
  }
  if (animalMedia.fileType.startsWith('video')) {
    const videoLink = (
      <Link
        href={animalMedia.url}
        underline="hover"
        rel="noopener"
        target="_blank"
        variant="body2"
      >
        {getUrlParameterByName('name', animalMedia.url) || `Video ${index}`}
      </Link>
    );
    if (!isAdmin) return videoLink;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        {videoLink}
        {animalMedia.hidden && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            (Hidden from community)
          </Typography>
        )}
        <Tooltip title={animalMedia.hidden ? 'Unhide' : 'Hide from community'}>
          <IconButton
            aria-label={animalMedia.hidden ? 'Unhide' : 'Hide from community'}
            size="small"
            onClick={toggleHidden}
            disabled={loading}
          >
            {animalMedia.hidden
              ? <VisibilityOutlined fontSize="inherit" />
              : <VisibilityOffOutlined fontSize="inherit" />}
          </IconButton>
        </Tooltip>
        {errorSnackbar}
      </Box>
    );
  }
  return wrapTile(
    <Button
      sx={TILE_SX}
      onClick={() => {
        setDialogImg(animalMedia.url);
        setDialogDate(animalMedia.dateCreated);
        setImgDialogOpen(true);
      }}
    >
      <img
        style={{ objectFit: 'cover', objectPosition: 'center' }}
        key={animalMedia.url}
        src={animalMedia.url}
        alt={`${animalName}-${index}`}
        loading="lazy"
        width="80px"
        height="80px"
      />
    </Button>,
  );
}

export default MediaThumbnail;
